/**
 * DogVideoUploader — manages up to 3 short working-dog clips on an
 * individual dog profile (sire/dam).
 *
 * Why this lives on the dog, not the kennel: buyers research the
 * specific sire/dam before committing to a litter. A bitework reel
 * on the dam's profile sits right next to her pedigree — exactly
 * where the buyer makes the decision. The kennel-level hero video
 * (EliteKennelEditor) is for *brand*; this is for *evidence*.
 *
 * Two upload modes:
 *   • File upload (MP4/MOV/WEBM, ≤100MB) → posted to /api/storage/upload
 *   • YouTube / Vimeo URL → embedded as iframe on public page
 *
 * The backend auto-detects `kind` from the URL host so the breeder
 * can just paste a link without picking a dropdown.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, YoutubeLogoIcon as YoutubeLogo, FilmSlateIcon as FilmSlate, UploadSimpleIcon as UploadSimple, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/utils-app";

const MAX_VIDEOS = 3;
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB per video

export default function DogVideoUploader({ dogId, videos = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [embedCaption, setEmbedCaption] = useState("");
  // ToS §7a — the seller must affirmatively confirm sport/training
  // context, person-in-frame consent, and rights to audio + footage
  // BEFORE we accept the upload. Backend re-enforces; this gate also
  // protects us from accidental clicks. The attestation is per-session
  // (sticky across multiple uploads in one sitting) — once the breeder
  // has read the warranty for this dog, we don't make them re-tick it
  // for the next clip.
  const [attested, setAttested] = useState(false);
  const fileInputRef = useRef(null);

  const remaining = MAX_VIDEOS - (videos?.length || 0);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!attested) {
      toast.error("Please tick the working-video acknowledgement before uploading.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Video must be ≤ 100MB. Trim to a 10–30s clip for best results.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = up?.data?.url || up?.data?.storage_path;
      if (!url) throw new Error("Upload failed");
      const { data } = await api.post(`/dogs/${dogId}/videos`, {
        kind: "file",
        url,
        title: file.name?.replace(/\.[^.]+$/, "").slice(0, 60) || "",
        attested: true,
      });
      onChange?.([...(videos || []), data]);
      toast.success("Video added — buyers can now see this dog in action.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addEmbed = async () => {
    const url = embedUrl.trim();
    if (!url) return;
    if (!attested) {
      toast.error("Please tick the working-video acknowledgement before adding a link.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/dogs/${dogId}/videos`, {
        url,
        caption: embedCaption.trim(),
        attested: true,
      });
      onChange?.([...(videos || []), data]);
      setEmbedUrl("");
      setEmbedCaption("");
      toast.success("Video link added — it will appear on the public profile.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (videoId) => {
    if (!window.confirm("Remove this video from the dog's profile?")) return;
    setBusy(true);
    try {
      await api.delete(`/dogs/${dogId}/videos/${videoId}`);
      onChange?.((videos || []).filter((v) => v.id !== videoId));
      toast.success("Video removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="dog-video-uploader">
      {/* Encouragement copy — sellers convert when they understand WHY
          they should bother. Lead with the conversion benefit, not the
          features. */}
      <div className="rounded-xl border border-[#E8D9A6] bg-[#FBF7EC] p-4 mb-4">
        <div className="flex items-start gap-2.5">
          <Sparkle size={18} weight="fill" className="text-[#8A6A00] flex-shrink-0 mt-0.5" />
          <div className="text-[13px] leading-snug text-[#3F3F3F]">
            <p className="font-bold text-ink mb-0.5">Show buyers what this dog can do.</p>
            <p>
              A short clip of <em>bitework, hunt drive, agility, retrieve, or just clean
              obedience</em> answers a buyer&apos;s #1 unspoken question:{" "}
              <strong>&ldquo;Is the temperament real?&rdquo;</strong> Photos prove looks;
              video proves drive. Upload 1–3 clips and they appear right next to the
              pedigree on the public profile.
            </p>
          </div>
        </div>
      </div>

      {/* Existing videos */}
      {(videos || []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} onRemove={() => remove(v.id)} disabled={busy} />
          ))}
        </div>
      )}

      {remaining > 0 ? (
        <>
          {/* Attestation gate — ToS §7a working-video warranty. Hidden
              after they tick once per session so we don't nag them on
              every clip. The Submit button still requires `attested`
              so a deselect re-blocks the action. */}
          <div
            className={`mb-4 rounded-lg border-2 px-3 py-2.5 ${
              attested ? "border-emerald-200 bg-emerald-50/60" : "border-amber-300 bg-amber-50"
            }`}
            data-testid="dog-video-attestation-row"
          >
            <label className="flex items-start gap-2 cursor-pointer text-[12.5px] leading-snug text-ink">
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
                data-testid="dog-video-attestation-checkbox"
                className="mt-0.5 flex-shrink-0 h-4 w-4 accent-crimson cursor-pointer"
              />
              <span>
                <strong>I confirm</strong> this video depicts sanctioned canine
                sport, training, or routine handling (not aggression-for-sale or
                cruelty), any identifiable person in frame has consented to
                appear, and I hold all rights to the audio and footage. See{" "}
                <a
                  href="/legal/terms#working-video"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-semibold hover:text-crimson"
                  data-testid="dog-video-attestation-tos-link"
                >
                  Terms §7a &quot;Working / sport video&quot;
                </a>
                .
              </span>
            </label>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity ${
              attested ? "opacity-100" : "opacity-50 pointer-events-none"
            }`}
            aria-disabled={!attested}
          >
          {/* File upload */}
          <div className="rounded-lg border-2 border-dashed border-[#D4C8A6] p-4 bg-white/50">
            <div className="flex items-center gap-1.5 mb-2">
              <UploadSimple size={15} weight="bold" className="text-ink" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-ink">
                Upload a clip
              </span>
            </div>
            <p className="text-[11.5px] text-[#6B6B6B] mb-2 leading-snug">
              MP4, MOV, or WEBM. Up to 100MB. Sweet spot: 10–30 seconds, shot horizontally.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              disabled={busy}
              data-testid="dog-video-file-input"
              className="text-[12px] text-ink w-full"
            />
          </div>

          {/* YouTube / Vimeo embed */}
          <div className="rounded-lg border-2 border-dashed border-[#D4C8A6] p-4 bg-white/50">
            <div className="flex items-center gap-1.5 mb-2">
              <YoutubeLogo size={15} weight="fill" className="text-red-600" />
              <FilmSlate size={15} weight="fill" className="text-sky-600" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-ink">
                Or paste a YouTube/Vimeo link
              </span>
            </div>
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=…"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              disabled={busy}
              data-testid="dog-video-embed-url-input"
              className="h-9 text-[12.5px] mb-1.5"
            />
            <Input
              type="text"
              placeholder="Caption — e.g. IPO bitework at 22 months"
              value={embedCaption}
              onChange={(e) => setEmbedCaption(e.target.value)}
              maxLength={120}
              disabled={busy}
              data-testid="dog-video-embed-caption-input"
              className="h-9 text-[12.5px] mb-2"
            />
            <Button
              type="button"
              onClick={addEmbed}
              disabled={busy || !embedUrl.trim()}
              size="sm"
              className="h-8 px-3 text-[12px] bg-ink text-white hover:bg-crimson rounded-full"
              data-testid="dog-video-embed-add-btn"
            >
              <Plus size={12} weight="bold" className="mr-1" /> Add link
            </Button>
          </div>
        </div>
        </>
      ) : (
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-[12px] text-stone-600">
          You&apos;ve added the maximum of {MAX_VIDEOS} videos. Remove one above to add another.
        </div>
      )}
      <p className="mt-3 text-[11px] text-[#6B6B6B] leading-snug">
        <strong>Up to {MAX_VIDEOS} videos.</strong> Avoid copyrighted music — we may remove a video
        on a valid DMCA notice. No people in the frame other than the handler. No reactive /
        bite-and-hold work without context (write a caption explaining the title or sport).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual video tile — handles file <video> and YouTube/Vimeo iframes.
// ---------------------------------------------------------------------------

function VideoCard({ video, onRemove, disabled }) {
  const isFile = video.kind === "file";
  const isYouTube = video.kind === "youtube";
  const isVimeo = video.kind === "vimeo";

  return (
    <div
      className="rounded-lg overflow-hidden border border-stone-200 bg-white relative group"
      data-testid={`dog-video-card-${video.id}`}
    >
      <div className="aspect-video bg-black relative">
        {isFile && (
          <video
            src={video.url}
            poster={video.poster_url || undefined}
            controls
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        )}
        {isYouTube && (
          <iframe
            src={toYouTubeEmbed(video.url)}
            title={video.title || "Working footage"}
            allow="accelerometer; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            loading="lazy"
          />
        )}
        {isVimeo && (
          <iframe
            src={toVimeoEmbed(video.url)}
            title={video.title || "Working footage"}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            loading="lazy"
          />
        )}
      </div>
      {(video.title || video.caption) && (
        <div className="px-2.5 py-1.5">
          {video.title && (
            <p className="text-[12px] font-semibold text-ink leading-tight truncate">{video.title}</p>
          )}
          {video.caption && (
            <p className="text-[11px] text-[#6B6B6B] leading-snug">{video.caption}</p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        title="Remove this video"
        data-testid={`dog-video-remove-${video.id}`}
        className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-crimson transition flex items-center justify-center disabled:opacity-30"
      >
        <Trash size={13} weight="bold" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YouTube / Vimeo URL → embed URL normalisation.
//
// Accepts the messy variety of share URLs the user might paste:
//   • https://www.youtube.com/watch?v=XXX
//   • https://youtu.be/XXX
//   • https://www.youtube.com/shorts/XXX
//   • https://vimeo.com/XXX
// Returns a URL safe to drop into an <iframe src>.
// ---------------------------------------------------------------------------

function toYouTubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.pathname.startsWith("/shorts/")) {
      const id = u.pathname.split("/")[2] || "";
      return `https://www.youtube.com/embed/${id}`;
    }
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    return url;
  } catch {
    return url;
  }
}

function toVimeoEmbed(url) {
  try {
    const u = new URL(url);
    const id = u.pathname.replace(/^\//, "").split("/")[0];
    if (id) return `https://player.vimeo.com/video/${id}`;
    return url;
  } catch {
    return url;
  }
}

// Re-export for the public dog page so we don't duplicate the same
// normalisation logic.
export { toYouTubeEmbed, toVimeoEmbed };
